import { useQuery } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';
import { api } from '../../lib/api';
import { useT, type TranslationKey } from '../../i18n';
import { formatMoneyShort, formatNumber } from '../../lib/reportFormat';
import {
  inquiryAmount,
  inquiryRef,
  type Confidence,
  type Inquiry,
  type ProductType,
  type Stage,
} from '../../types/api';
import { EmptyState } from '../EmptyState';
import { EntityLink } from '../EntityLink';
import { Modal } from '../Modal';
import { Skeleton } from '../Skeleton';
import { stageLabelKey } from '../../lib/stageLabels';
import styles from './BasketDrillIn.module.css';

const CONF_NAME_KEY: Record<Confidence, TranslationKey> = {
  K90: 'confidence.k90Label',
  K50: 'confidence.k50Label',
  K10: 'confidence.k10Label',
};

const CONF_LABEL_KEY: Record<Confidence, TranslationKey> = {
  K90: 'confidence.k90',
  K50: 'confidence.k50',
  K10: 'confidence.k10',
};

/** Basket deals live at stages 3–4 (per approved dashboard mockup). */
const BASKET_STAGES: Stage[] = ['PRIORITIZATION', 'REALIZATION'];

export interface BasketDrillInProps {
  open: boolean;
  basket: Confidence | null;
  /** ISO date bounds of the dashboard period. */
  from: string;
  to: string;
  /** Basket size in the current portfolio snapshot (all open deals with this confidence). */
  totalCount: number;
  /** Selected Dashboard tab (#75) — stage labels follow the route wording. */
  productType?: ProductType | null;
  onClose: () => void;
}

/**
 * Drill-in for a confidence basket: deals that entered stages 3–4 during
 * the period with this confidence rating (GET /api/reports/pipeline/group).
 */
export function BasketDrillIn({ open, basket, from, to, totalCount, productType, onClose }: BasketDrillInProps) {
  const { t, locale } = useT();

  const dealsQ = useQuery<Inquiry[]>({
    queryKey: ['reports', 'pipeline', 'basket', basket, from, to],
    enabled: open && basket !== null,
    queryFn: async () => {
      const lists = await Promise.all(
        BASKET_STAGES.map((stage) =>
          api.get<Inquiry[]>('/reports/pipeline/group', {
            query: { from, to, stage, group: 'in', confidence: basket ?? undefined },
          }),
        ),
      );
      // Same inquiry can enter both stages in-period — dedupe by id.
      const byId = new Map<string, Inquiry>();
      for (const inq of lists.flat()) byId.set(inq.id, inq);
      return [...byId.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    },
  });

  const deals = dealsQ.data ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        basket
          ? `${t(CONF_LABEL_KEY[basket])} · ${t(CONF_NAME_KEY[basket])}`
          : t('pipeline.drillInTitle')
      }
      description={
        dealsQ.isSuccess
          ? `${t('pipeline.drillInScopeNote')} · ${t('pipeline.drillInOfCount', {
              shown: formatNumber(deals.length, locale),
              total: formatNumber(totalCount, locale),
            })}`
          : undefined
      }
    >
      {dealsQ.isPending && (
        <div className={styles.skeletons}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="block" height={56} radius="lg" />
          ))}
        </div>
      )}

      {dealsQ.isError && (
        <EmptyState
          compact
          icon={<Inbox size={22} />}
          title={t('pipeline.errorTitle')}
          description={dealsQ.error.message}
        />
      )}

      {dealsQ.isSuccess && deals.length === 0 && (
        <EmptyState
          compact
          icon={<Inbox size={22} />}
          title={t('pipeline.drillInEmpty')}
        />
      )}

      {dealsQ.isSuccess && deals.length > 0 && (
        <ul className={styles.list}>
          {deals.map((d) => {
            const amount = inquiryAmount(d);
            return (
              <li className={styles.deal} key={d.id}>
                <div className={styles.dealMain}>
                  <div className={styles.dealClient}>
                    <EntityLink
                      kind="client"
                      id={d.client?.id ?? d.clientId}
                      name={d.client?.companyName}
                      fallback={inquiryRef(d)}
                    />
                  </div>
                  <div className={styles.dealWhat}>{d.summary}</div>
                </div>
                <div className={styles.dealMeta}>
                  <span className={`${styles.stageChip} ${styles[`stage_${d.stage}`]}`}>
                    {t(stageLabelKey(d.stage, productType))}
                  </span>
                  {d.confidenceReason && (
                    <span className={styles.dealReason}>{d.confidenceReason}</span>
                  )}
                </div>
                <span className={styles.dealSum}>
                  {amount !== null && amount > 0
                    ? formatMoneyShort(amount, locale, t('pipeline.millionShort'))
                    : t('pipeline.drillInNoAmount')}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
